"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SWATCH_ROLES = void 0;
exports.readThemeSwatch = readThemeSwatch;
exports.readThemeSwatches = readThemeSwatches;
const theme_1 = require("./theme");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A THEME'S PREVIEW COLOURS, DERIVED FROM THE TOKENS THAT DEFINE IT.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The theme gallery shows a small swatch per theme — page, sidebar, card, accent, text. The mockup
 * hardcodes those as hex, one literal per theme per role (`pv: { bg: '#ffffff', … }`), and
 * `docs/plan/debt.md` has had *"derive the theme-picker swatches"* open since C1 for the obvious
 * reason: **a hardcoded preview is a second definition of the palette.** Change a token and the
 * swatch keeps showing the old colour, so the one screen whose whole job is showing you what a
 * theme looks like is the one screen that lies about it.
 *
 * CLAUDE.md is blunter still — *"no hardcoded user-facing strings"* has a colour equivalent in the
 * design system's `no-raw-hex`, and forty hex literals is exactly what that forbids.
 *
 * ⚠️ **So the swatches are MEASURED, not listed.** `applyTheme` already takes a root element, so a
 * detached element can be themed and read with `getComputedStyle`. What comes back is whatever the
 * token layer actually resolves to today, for every theme, with no list to keep in step.
 *
 * ⚠️ **This needs a DOM and a stylesheet, which is the one real cost.** In jsdom no stylesheet is
 * applied, so every value comes back empty — the function reports that honestly rather than
 * inventing a colour, and the gallery renders a neutral placeholder. A test asserting specific hex
 * in jsdom would be asserting nothing.
 */
/** The five roles the mockup's own preview draws, in the order it draws them. */
exports.SWATCH_ROLES = ['background', 'sidebar', 'card', 'primary', 'foreground'];
/**
 * ⚠️ **The custom property behind each role.** Not a colour — the *name* of one. This map is the
 * only place the two vocabularies meet, and it is four lines rather than forty.
 */
const TOKEN = {
    background: '--background',
    sidebar: '--sidebar',
    card: '--card',
    primary: '--primary',
    foreground: '--foreground',
};
/**
 * Read one theme's swatch by applying it to a detached element.
 *
 * ⚠️ **`position: fixed` and `visibility: hidden`, appended to `<body>` and removed again.** It has
 * to be *in* the document for `getComputedStyle` to resolve custom properties at all — a truly
 * detached node resolves nothing — and it must not affect layout for the frame it exists.
 * `display: none` would be simpler and does not work: a `display: none` subtree still resolves
 * custom properties, but browsers are permitted to skip style resolution for it, and Safari does.
 */
function readThemeSwatch(theme, doc = document) {
    const probe = doc.createElement('div');
    probe.style.cssText =
        'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;visibility:hidden;pointer-events:none';
    doc.body.appendChild(probe);
    try {
        (0, theme_1.applyTheme)(theme, probe);
        const computed = doc.defaultView?.getComputedStyle(probe);
        if (computed === undefined)
            return null;
        const swatch = {
            background: '',
            sidebar: '',
            card: '',
            primary: '',
            foreground: '',
        };
        for (const role of exports.SWATCH_ROLES) {
            swatch[role] = computed.getPropertyValue(TOKEN[role]).trim();
        }
        /**
         * ⚠️ **All or nothing, deliberately.** A partial swatch — three roles resolved, two empty —
         * renders as a preview with holes in it, which reads as a broken theme rather than a missing
         * stylesheet. Under jsdom every value is empty and this returns `null`, which is the honest
         * answer: nobody applied a stylesheet, so there is nothing to show.
         */
        return exports.SWATCH_ROLES.every((role) => swatch[role] !== '') ? swatch : null;
    }
    finally {
        /* `finally`, so a throw in `applyTheme` cannot leave a probe node in the document. Left behind
         * once per render, this would be a slow leak nobody would connect to a theme picker. */
        probe.remove();
    }
}
/**
 * Every theme's swatch, in `THEMES` order.
 *
 * ⚠️ **One probe element for all of them, not one each.** The gallery renders eight; eight
 * append/remove cycles is eight forced style recalculations, and this runs on every mount of the
 * Theme tab.
 */
function readThemeSwatches(doc = document) {
    const swatches = new Map();
    const probe = doc.createElement('div');
    probe.style.cssText =
        'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;visibility:hidden;pointer-events:none';
    doc.body.appendChild(probe);
    try {
        const computed = doc.defaultView?.getComputedStyle(probe);
        if (computed === undefined)
            return swatches;
        for (const theme of theme_1.THEMES) {
            (0, theme_1.applyTheme)(theme.id, probe);
            const swatch = {
                background: '',
                sidebar: '',
                card: '',
                primary: '',
                foreground: '',
            };
            for (const role of exports.SWATCH_ROLES) {
                swatch[role] = computed.getPropertyValue(TOKEN[role]).trim();
            }
            if (exports.SWATCH_ROLES.every((role) => swatch[role] !== ''))
                swatches.set(theme.id, swatch);
        }
    }
    finally {
        probe.remove();
    }
    return swatches;
}
//# sourceMappingURL=theme-swatches.js.map